import { parseNameTime } from "../components/DataView";

// Builds the set of performers present in the currently scraped roster data, as
// "venue::name" keys. Used to decide whether a subscription can link to a profile:
// details and photos are only captured while someone is being rostered, so linking
// a performer who has dropped off the roster risks opening an empty profile.
//
// Covers today and tomorrow, matching what the roster tables display. Names are run
// through parseNameTime so the keys line up with what NotifyButton stored when the
// user subscribed.

const key = (venue, name) => `${venue}::${name}`.trim().toLowerCase();

const namesFrom = (entries) =>
  (entries || [])
    .map((entry) => parseNameTime(entry).name)
    .map((name) => (name || "").replace(/[\s-]+$/, "").trim())
    .filter(Boolean);

export function buildRosteredKeys(data) {
  const keys = new Set();
  if (!data) return keys;

  const add = (venue, entries) => {
    namesFrom(entries).forEach((name) => keys.add(key(venue, name)));
  };

  add("marrickville", data.no5?.today);
  add("marrickville", data.no5?.tomorrow);

  add("gladesville", data.nightshade?.today);
  add("gladesville", data.nightshade?.tomorrow);

  // The Ginza-template venues expose rosters as an ordered [today, tomorrow] list.
  (data.ginza?.rosters || []).forEach((block) => add("cleveland", block?.names));
  (data.ginza479?.rosters || []).forEach((block) => add("elizabeth", block?.names));

  return keys;
}

export function isRostered(rosteredKeys, venue, performerName) {
  if (!rosteredKeys) return false;
  return rosteredKeys.has(key(venue, (performerName || "").replace(/[\s-]+$/, "")));
}

// Single place that knows how each venue family splits today vs tomorrow: the
// timetable venues use `today`/`tomorrow` arrays, the Ginza venues an ordered
// `rosters` list where [0] is today and [1] is tomorrow.
function dayEntries(data, venue) {
  if (!data) return { today: [], tomorrow: [] };
  switch (venue) {
    case "marrickville":
      return { today: data.no5?.today || [], tomorrow: data.no5?.tomorrow || [] };
    case "gladesville":
      return {
        today: data.nightshade?.today || [],
        tomorrow: data.nightshade?.tomorrow || [],
      };
    case "cleveland": {
      const blocks = data.ginza?.rosters || [];
      return { today: blocks[0]?.names || [], tomorrow: blocks[1]?.names || [] };
    }
    case "elizabeth": {
      const blocks = data.ginza479?.rosters || [];
      return { today: blocks[0]?.names || [], tomorrow: blocks[1]?.names || [] };
    }
    default:
      return { today: [], tomorrow: [] };
  }
}

const normalise = (name) => (name || "").replace(/[\s-]+$/, "").trim().toLowerCase();

// A day's roster counts as published only if at least one entry carries a time.
// Before a venue publishes tomorrow, the timetable sites emit a single placeholder
// row ("Monday's roster will be available at 7pm") with a null time, and the Ginza
// sites return an empty list - so "nobody has a time" distinguishes "not out yet"
// from "out, and this person isn't on it". Those read very differently to a user.
const isPublished = (entries) =>
  (entries || []).some((entry) => (parseNameTime(entry).start || "").trim());

/**
 * Today's and tomorrow's shift for one performer at one venue.
 *
 * Returns { today, tomorrow } where each is:
 *   { status: "working", time }   - on that day's roster
 *   { status: "off" }             - roster is out, they're not on it
 *   { status: "unpublished" }     - that day's roster isn't available yet
 */
export function getSchedule(data, venue, performerName) {
  const target = normalise(performerName);
  const { today, tomorrow } = dayEntries(data, venue);

  const dayStatus = (entries) => {
    if (!isPublished(entries)) return { status: "unpublished" };
    const match = (entries || []).find(
      (entry) => normalise(parseNameTime(entry).name) === target
    );
    if (!match) return { status: "off" };
    const parsed = parseNameTime(match);
    const range = [parsed.start, parsed.finish].filter(Boolean).join(" – ");
    return { status: "working", time: range || (match.time || "").trim() };
  };

  return { today: dayStatus(today), tomorrow: dayStatus(tomorrow) };
}
